import React, { useState } from "react";
import { X, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../lib/CartContext";
import { useAuth, loginWithGoogle } from "../lib/AuthContext";
import { api, formatPrice, imgUrl } from "../lib/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { open, setOpen, items, remove, total } = useCart();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const checkout = async () => {
    if (!user) { loginWithGoogle(); return; }
    if (items.length === 0) return;
    setLoading(true);
    try {
      // Redirect to full cart page to complete shipping address + place order
      setOpen(false);
      navigate("/cart");
    } catch (e) {
      toast.error("Eroare");
      setLoading(false);
    }
  };

  return (
    <>
      <div className={`fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm transition-opacity duration-500 ${open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`} onClick={() => setOpen(false)} />
      <aside className={`fixed top-0 right-0 bottom-0 z-[80] w-full sm:w-[440px] bg-[#0a0a0a] border-l border-[#D4AF37]/15 transition-transform duration-500 ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`} data-testid="cart-drawer">
        <div className="flex items-center justify-between p-6 border-b border-[#D4AF37]/15">
          <div>
            <p className="label-luxe">Coșul Tău</p>
            <h3 className="font-serif text-2xl text-[#F8F4E6] mt-1">{items.length} {items.length === 1 ? "obiect" : "obiecte"}</h3>
          </div>
          <button onClick={() => setOpen(false)} className="text-[#A39E8A] hover:text-[#D4AF37]" data-testid="close-cart"><X size={20} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="text-center py-20">
              <p className="font-serif text-2xl text-[#A39E8A] mb-3">Coșul este gol</p>
              <p className="text-sm text-[#A39E8A]/70 mb-8">Începe să descoperi piesele rare ale colecției.</p>
              <Link to="/shop" onClick={() => setOpen(false)} className="btn-ghost">Explorează</Link>
            </div>
          ) : (
            <ul className="space-y-6">
              {items.map(({ product, quantity }) => (
                <li key={product.id} className="flex gap-4 pb-6 border-b border-[#D4AF37]/10" data-testid={`cart-item-${product.id}`}>
                  <Link to={`/products/${product.id}`} onClick={() => setOpen(false)} className="w-24 h-28 flex-shrink-0 bg-[#0F0F0F]">
                    <img src={imgUrl(product.images?.[0])} alt={product.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1 min-w-0">
                    <p className="label-luxe">{product.category}</p>
                    <h4 className="font-serif text-base text-[#F8F4E6] mt-1 leading-tight line-clamp-2">{product.name}</h4>
                    <p className="text-xs text-[#A39E8A] mt-2">Cantitate: {quantity}</p>
                    <div className="flex items-center justify-between mt-3">
                      <span className="font-serif text-lg text-[#D4AF37]">{formatPrice(product.price * quantity)}</span>
                      <button onClick={() => remove(product.id)} className="text-[#A39E8A] hover:text-red-400 transition" data-testid={`remove-${product.id}`}>
                        <Trash2 size={14} strokeWidth={1.4} />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <div className="p-6 border-t border-[#D4AF37]/15 space-y-4">
            <div className="flex justify-between items-baseline">
              <span className="label-luxe">Subtotal</span>
              <span className="font-serif text-3xl text-[#D4AF37]">{formatPrice(total)}</span>
            </div>
            <p className="text-[11px] text-[#A39E8A] tracking-wider">Taxe și transport calculate la finalizare.</p>
            <button onClick={checkout} disabled={loading} className="btn-gold w-full justify-center" data-testid="checkout-button">
              {loading ? "Se procesează..." : "Finalizează Comanda"}
            </button>
            <button onClick={() => { setOpen(false); navigate("/cart"); }} className="text-center w-full text-[11px] uppercase tracking-[0.25em] text-[#A39E8A] hover:text-[#D4AF37]">
              Vezi Coșul Complet
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
